// The open / résolus / tous filter chips. Each chip drives `setFilter`, which
// re-fetches from the BE with the matching `resolved=` query (the filter is a
// SERVER decision, W1 — never a local computation). Active-chip styling via
// logical utilities only (§9).

import { useTranslation } from 'react-i18next'
import type { ThreadFilter } from '@/domain/comments/entity'

interface CommentFilterChipsProps {
  filter: ThreadFilter
  onChange: (filter: ThreadFilter) => void
  disabled?: boolean
}

const FILTERS: ReadonlyArray<{ value: ThreadFilter; labelKey: string }> = [
  { value: 'open', labelKey: 'comments.filter.open' },
  { value: 'resolved', labelKey: 'comments.filter.resolved' },
  { value: 'all', labelKey: 'comments.filter.all' },
]

export function CommentFilterChips({
  filter,
  onChange,
  disabled = false,
}: CommentFilterChipsProps): JSX.Element {
  const { t } = useTranslation()
  return (
    <div
      data-testid="comments-filter-chips"
      role="tablist"
      className="flex items-center gap-1.5 border-b border-ink/10 px-3.5 py-2"
    >
      {FILTERS.map(({ value, labelKey }) => {
        const active = filter === value
        return (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            data-testid={`comments-filter-${value}`}
            onClick={() => onChange(value)}
            className={[
              'rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors',
              active
                ? 'bg-ink text-white'
                : 'bg-ink/5 text-ink/60 hover:bg-ink/10',
              disabled ? 'cursor-not-allowed opacity-50' : '',
            ].join(' ')}
          >
            {t(labelKey)}
          </button>
        )
      })}
    </div>
  )
}
